import React, { useMemo, useCallback } from 'react';
import { Row, Col, Button } from 'antd';
import { SettingOutlined, PlusCircleOutlined } from '@ant-design/icons';
import _ from 'lodash';
import { ReactSortable } from 'react-sortablejs';

import Parts from './Parts';
import Miscellaneous from './Miscellaneous';
import { AddsContainer } from './common';
import {
  createHandleDragEnd,
  onlyAllowType,
} from '../util/CreateHandleDragEnd';
import EmptyItem from './EmptyItem';
import { v4 as uuidv4 } from 'uuid';

const MemoizedEmptyItem = React.memo(EmptyItem);

const MiscContainer = React.memo(function MiscContainer({
  itemKey,
  miscs,
  addSubItem,
  deleteSubItem,
  changeSubItem,
  moveItem,
}) {
  const handleDragEnd = useMemo(
    () =>
      createHandleDragEnd({
        items: miscs,
        type: 'miscellaneous',
        itemKey,
        moveItem,
      }),
    [miscs, itemKey, moveItem]
  );

  const sortedMiscs = useMemo(
    () =>
      [...(miscs || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [miscs]
  );

  const handleAddMisc = useCallback(() => {
    const maxPosition = sortedMiscs.reduce(
      (max, misc) => Math.max(max, misc.position ?? 0),
      0
    );
    addSubItem('miscellaneous', {
      key: uuidv4(),
      id: _.random(100000),
      description: '',
      quantity: 1,
      unit_cost: 0,
      position: maxPosition + 1,
    });
  }, [addSubItem, sortedMiscs]);

  const setListGuardRef = React.useRef({});

  return (
    <Row
      style={{ margin: '10px' }}
      gutter={16}
      className="misc-container"
      data-lineitem={itemKey.toString()}
    >
      <Col span={24}>
        <AddsContainer>
          <Row type="flex" align="middle" style={{ alignItems: 'flex-start' }}>
            <Col style={{ width: 40, textAlign: 'center', paddingTop: '25px' }}>
              <SettingOutlined style={{ fontSize: '32px' }} />
            </Col>
            <Col style={{ flex: 1, paddingLeft: 5 }}>
              <Row>
                <Col span={11} style={{ borderBottom: '1px solid #d9d9d9' }}>
                  Misc:
                </Col>
                <Col
                  span={2}
                  style={{
                    paddingRight: '20px',
                    borderBottom: '1px solid #d9d9d9',
                  }}
                >
                  Hours:
                </Col>
                <Col
                  span={5}
                  style={{
                    paddingRight: '20px',
                    paddingLeft: '20px',
                    borderBottom: '1px solid #d9d9d9',
                  }}
                >
                  Rate:
                </Col>
                <Col
                  span={5}
                  style={{
                    paddingLeft: '20px',
                    borderBottom: '1px solid #d9d9d9',
                  }}
                >
                  Amount:
                </Col>

                <Col span={1}></Col>
              </Row>
              <ReactSortable
                tag="div"
                list={miscs || []}
                setList={(newList) => {
                  if (!setListGuardRef.current[itemKey]) {
                    setListGuardRef.current[itemKey] = true;
                    return;
                  }
                  moveItem(itemKey, 'miscellaneous', newList);
                }}
                onEnd={handleDragEnd}
                group={{
                  name: 'sharedMiscs',
                  pull: true,
                  put: onlyAllowType('misc'),
                }}
                animation={150}
                data-lineitem={itemKey.toString()}
                scroll={true}
                scrollSensitivity={100}
                scrollSpeed={10}
                forceFallback={true}
                onMove={(evt) => {
                  const target = evt.related;
                  return !target?.classList.contains('misc-container');
                }}
                scrollTarget={() => document.querySelector('.wrapper-wo')}
                ghostClass="ghost"
                chosenClass="chosen"
                dragClass="dragging"
                filter=".no-move"
                preventOnFilter={true}
                tagProps={{ 'data-lineitem': itemKey }}
              >
                {sortedMiscs.length === 0 ? (
                  <MemoizedEmptyItem message="Drag and drop misc items here or add manually" />
                ) : (
                  sortedMiscs.map((item) => (
                    <div
                      key={item.key}
                      data-id={item.key.toString()}
                      data-type="misc"
                    >
                      <Miscellaneous
                        item={item}
                        deleteSubItem={() => {
                          deleteSubItem(itemKey, 'miscellaneous', item.key);
                        }}
                        changeSubItem={(value) =>
                          changeSubItem(
                            itemKey,
                            'miscellaneous',
                            item.key,
                            value
                          )
                        }
                      />
                    </div>
                  ))
                )}
              </ReactSortable>
            </Col>
          </Row>
        </AddsContainer>
        <Row>
          <Col style={{ width: 40, textAlign: 'center', marginTop: '20px' }}>
            {' '}
          </Col>
          <Col style={{ flex: 1 }}>
            <Button
              type="link"
              style={{ paddingLeft: '12px' }}
              icon={<PlusCircleOutlined />}
              onClick={handleAddMisc}
              data-testid="misc-container-add-misc-button"
            >
              Add Misc
            </Button>
          </Col>
        </Row>
      </Col>
    </Row>
  );
});

export default MiscContainer;
